import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Switch,
} from '@mui/material';
import { AttachMoney as AttachMoneyIcon, Payment as PaymentIconMui } from '@mui/icons-material';
import PaymentIcon from '../ui/PaymentIcon';
import placeServicePaymentsService from '../../services/placeServicePaymentsService';
import placeServicePaymentProfessionalsService from '../../services/placeServicePaymentProfessionalsService';
import paymentsService from '../../services/paymentsService';
import { formatCurrency } from '../../utils/formatters';

/**
 * ProfessionalCustomPricesDialog
 *
 * Dialog para gerenciar preços customizados de um profissional para um serviço+local.
 * Para cada forma de pagamento configurada no local, permite definir um preço que
 * sobrescreve o padrão do local (place_service_payment.default_price).
 *
 * Hierarquia: custom_price (profissional) → default_price (local) → default_price (serviço)
 */
export default function ProfessionalCustomPricesDialog({
  open,
  onClose,
  placeServiceLink,
  placeId,
  onUpdate,
}) {
  const [paymentItems, setPaymentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && placeServiceLink) {
      loadData();
    }
  }, [open, placeServiceLink]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar pagamentos configurados para o serviço neste local e metadados globais em paralelo
      const [configuredPayments, allPayments] = await Promise.all([
        placeServicePaymentsService.getByPlaceAndService(placeId, placeServiceLink.serviceId),
        paymentsService.getAll(),
      ]);

      // Montar lookup de metadados globais (isGlobal, displayOrder) por paymentId
      const paymentMeta = {};
      allPayments.forEach(p => {
        paymentMeta[p.id] = p;
      });

      const customPrices = placeServiceLink.customPrices || [];

      // Para cada pagamento configurado no local, verificar se já existe preço customizado
      const items = configuredPayments.map(cp => {
        const existing = customPrices.find(ep => ep.placeServicePaymentId === cp.id);
        const meta = paymentMeta[cp.paymentId] || {};
        return {
          placeServicePaymentId: cp.id,
          paymentId: cp.paymentId,
          paymentName: cp.paymentName || meta.name || 'Pagamento',
          placeDefaultPrice: cp.defaultPrice,
          // Overlay do profissional
          psppId: existing?.id || null,
          isEnabled: !!existing,
          customPrice: existing?.customPrice ?? '',
          // Metadata para ícone
          isGlobal: meta.isGlobal ?? true,
          displayOrder: meta.displayOrder || 1,
        };
      });

      setPaymentItems(items);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar formas de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (index) => {
    setPaymentItems(prev => prev.map((item, i) =>
      i === index ? { ...item, isEnabled: !item.isEnabled } : item
    ));
  };

  const handlePriceChange = (index, value) => {
    setPaymentItems(prev => prev.map((item, i) =>
      i === index ? { ...item, customPrice: value } : item
    ));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const item of paymentItems) {
        if (item.isEnabled) {
          const price = parseFloat(item.customPrice) || 0;
          if (item.psppId) {
            // Atualizar existente
            await placeServicePaymentProfessionalsService.update(item.psppId, {
              customPrice: price,
            });
          } else {
            // Criar novo
            await placeServicePaymentProfessionalsService.create({
              placeServiceProfessionalId: placeServiceLink.id,
              placeServicePaymentId: item.placeServicePaymentId,
              customPrice: price,
            });
          }
        } else if (item.psppId) {
          // Desativado mas existia — remover
          await placeServicePaymentProfessionalsService.delete(item.psppId);
        }
      }

      if (onUpdate) {
        await onUpdate();
      }

      onClose();
    } catch (err) {
      console.error('Erro ao salvar preços:', err);
      setError('Erro ao salvar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <AttachMoneyIcon color="primary" />
          <Box>
            <Typography variant="h6">
              Preços Customizados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {placeServiceLink?.professionalName} — {placeServiceLink?.serviceName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Defina preços específicos deste profissional para cada forma de pagamento.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Quando não há preço customizado, o padrão do local é utilizado.
              </Typography>
            </Alert>

            {paymentItems.length === 0 ? (
              <Alert severity="warning">
                Nenhuma forma de pagamento configurada para este serviço neste local.
                Configure as formas de pagamento do serviço primeiro.
              </Alert>
            ) : (
              <Box>
                {paymentItems.map((item, index) => (
                  <Box key={item.placeServicePaymentId} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {/* Ícone do pagamento */}
                      <Box sx={{ pt: 0.5 }}>
                        {item.isGlobal ? (
                          <PaymentIcon displayOrder={item.displayOrder} size={32} />
                        ) : (
                          <Box
                            sx={{
                              width: 32,
                              height: 21.33,
                              borderRadius: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontSize: '1rem',
                            }}
                          >
                            <PaymentIconMui sx={{ fontSize: '1rem' }} />
                          </Box>
                        )}
                      </Box>

                      {/* Nome e preço padrão */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1">
                          {item.paymentName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Padrão do local: {formatCurrency(item.placeDefaultPrice)}
                        </Typography>
                      </Box>

                      {/* Switch ativar/desativar */}
                      <Switch
                        checked={item.isEnabled}
                        onChange={() => handleToggle(index)}
                        size="small"
                      />

                      {/* Input de preço customizado */}
                      <TextField
                        size="small"
                        type="number"
                        value={item.isEnabled ? item.customPrice : ''}
                        onChange={(e) => handlePriceChange(index, e.target.value)}
                        disabled={!item.isEnabled}
                        placeholder={item.placeDefaultPrice?.toFixed(2) || '0.00'}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                        sx={{ width: 130 }}
                        inputProps={{
                          min: 0,
                          step: 0.01,
                          style: { textAlign: 'right' },
                        }}
                      />
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProfessionalCustomPricesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  placeServiceLink: PropTypes.object,
  placeId: PropTypes.string,
  onUpdate: PropTypes.func,
};
